import { useState } from 'react';
import { Building, Loader2, AlertCircle, Pencil, X, AlertTriangle, Plus, Lock } from 'lucide-react';
import {
  usePlatformOperators,
  useUpdateOperator,
  useCreateOperator,
  useAssignableProperties,
} from '@/hooks/api/usePlatform';
import type { PlatformOperator } from '@/services/api/platform.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Edit a group's display name and login code. Changing the code is highlighted
 * because it's what staff type at login — the server keeps the old code working
 * as an alias, and we say so, so BestAcco can rename confidently.
 */
function EditOperatorModal({
  operator,
  onClose,
}: {
  operator: PlatformOperator;
  onClose: () => void;
}) {
  const [name, setName] = useState(operator.name);
  const [groupCode, setGroupCode] = useState(operator.slug);
  const mutation = useUpdateOperator();

  const nameChanged = name.trim() !== operator.name;
  const codeChanged = groupCode.trim().toLowerCase() !== operator.slug.toLowerCase();
  const dirty = nameChanged || codeChanged;

  const submit = () => {
    const body: { name?: string; groupCode?: string } = {};
    if (nameChanged && name.trim()) body.name = name.trim();
    if (codeChanged && groupCode.trim()) body.groupCode = groupCode.trim();
    mutation.mutate(
      { tenantId: operator.tenantId, body },
      {
        onSuccess: (res) => {
          showToast(
            codeChanged
              ? `Group code is now "${res.groupCode}". The old code still works.`
              : 'Group updated.',
          );
          onClose();
        },
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-operator-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 id="edit-operator-title" className="text-base font-semibold text-gray-900">
            Edit group
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <Input
            label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint="Display name (e.g. rebrand a single hotel into a group)."
          />
          <Input
            label="Group code"
            value={groupCode}
            onChange={(e) => setGroupCode(e.target.value)}
            hint="What staff type at login. Lowercase letters, digits and hyphens; auto-normalized."
          />

          {codeChanged && (
            <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <AlertTriangle size={16} className="shrink-0 text-amber-500" aria-hidden="true" />
              <span>
                Staff will use the new code going forward. The old code
                (<span className="font-mono">{operator.slug}</span>) keeps working, so
                no one is locked out during the switch.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!dirty || mutation.isPending}>
            {mutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Saving…
              </span>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** name → group-code suggestion (matches the server's slug charset). */
function slugifyCode(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

/**
 * Create a new group (operator): name + login code, and optionally tick which
 * properties to place under it. Only DATA-FREE properties (no bookings) can be
 * moved — a property with live bookings is locked (its guests/invoices are
 * group-shared, so moving would orphan them). New hotels start data-free.
 */
function CreateGroupModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  const { data: properties, isLoading: propsLoading } = useAssignableProperties();
  const createMutation = useCreateOperator();

  const effectiveCode = codeTouched ? code : slugifyCode(name);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function submit() {
    setError('');
    if (!name.trim()) return setError('Enter a group name.');
    if (!effectiveCode.trim()) return setError('Enter a group code (letters/digits).');
    createMutation.mutate(
      { name: name.trim(), groupCode: effectiveCode.trim(), propertyIds: [...selected] },
      {
        onSuccess: (res) => {
          const parts = [`Group "${res.operator.name}" created (code: ${res.operator.groupCode})`];
          if (res.assigned.length) parts.push(`${res.assigned.length} propert${res.assigned.length === 1 ? 'y' : 'ies'} moved in`);
          if (res.skipped.length) parts.push(`${res.skipped.length} skipped (has live bookings)`);
          showToast(parts.join(' · '));
          onCreated();
          onClose();
        },
        onError: (e) => setError(extractErrorMessage(e)),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="create-group-title">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id="create-group-title" className="text-base font-semibold text-gray-900">Create group</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <Input
            label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Kentucky Hotels"
          />
          <Input
            label="Group code (staff type this at login)"
            value={effectiveCode}
            onChange={(e) => { setCodeTouched(true); setCode(e.target.value); }}
            placeholder="e.g. kentucky-hotels"
            className="font-mono"
            hint="Auto-filled from the name; edit if you like. Must be unique across all groups."
          />

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Add properties (optional)</p>
            <p className="text-xs text-gray-400 mb-2">
              Tick hotels to place under this group. Properties with live bookings are locked — their data is shared within their current group, so moving them isn&apos;t safe. Create new hotels directly into the group instead.
            </p>
            {propsLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Loading properties…
              </div>
            ) : !properties || properties.length === 0 ? (
              <p className="py-3 text-sm text-gray-400">No properties yet.</p>
            ) : (
              <ul className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {properties.map((p) => (
                  <li key={p.id}>
                    <label className={`flex items-center gap-3 px-3 py-2 ${p.movable ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'}`}>
                      <input
                        type="checkbox"
                        disabled={!p.movable}
                        checked={selected.has(p.id)}
                        onChange={() => toggle(p.id)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-800 truncate">{p.name}</span>
                        <span className="block text-xs text-gray-400 truncate">{p.city} · currently in {p.operatorName}</span>
                      </span>
                      {!p.movable && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 shrink-0" title="Has live bookings — can't be moved">
                          <Lock size={12} aria-hidden="true" /> has bookings
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
          <Button onClick={submit} isLoading={createMutation.isPending}>Create group</Button>
        </div>
      </div>
    </div>
  );
}

export function AdminOperatorsPage() {
  const { data, isLoading, isError, refetch } = usePlatformOperators();
  const [editing, setEditing] = useState<PlatformOperator | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Groups"
        description="A group (operator) is a company that manages one or more hotels on BestAcco. Its staff log in with the group code. One login sees every property in the group."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden="true" />
            Create group
          </Button>
        }
      />

      {creating && (
        <CreateGroupModal onClose={() => setCreating(false)} onCreated={() => void refetch()} />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading operators…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load operators.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState icon={Building} title="No operators yet" description="Operators appear here once a business is onboarded onto the platform." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Operators list">
              <thead>
                <tr className="bg-gray-50">
                  {['Operator', 'Group code', 'Properties', 'Status', 'Joined', ''].map((col) => (
                    <th
                      key={col || 'actions'}
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((op) => (
                  <tr key={op.tenantId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-100 shrink-0" aria-hidden="true">
                          <Building size={16} className="text-brand-600" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{op.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 font-mono">{op.slug}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{op.propertyCount}</td>
                    <td className="px-5 py-4">
                      <span
                        className={
                          op.isActive
                            ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700'
                            : 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500'
                        }
                      >
                        {op.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500">{formatDate(op.createdAt)}</td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => setEditing(op)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 transition-colors"
                      >
                        <Pencil size={14} aria-hidden="true" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditOperatorModal operator={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
